/**
 * Structured-detail parsing for the `reservation.expiry_deadline_updated`
 * audit event (CU-86cb7qgc9).
 *
 * The unit-tab audit timeline renders every event's `details` as a raw JSON
 * dump by default (`AuditEntryDetails` in `unit-reservations-tab.tsx`). This
 * module narrows the one well-known shape written by
 * `ReservationsService.editExpiryDeadline`'s audit call so the timeline can
 * show a structured old/new/reason block for it instead — any event whose
 * `details` do not match (missing keys, wrong types, or a different action)
 * falls back to the existing raw-JSON rendering unchanged.
 */
import { z } from 'zod';

/** The audit `action` string this module recognizes. */
export const EXPIRY_DEADLINE_UPDATED_ACTION = 'reservation.expiry_deadline_updated';

const ExpiryDeadlineAuditDetailsSchema = z.object({
  previousExpiryDeadline: z.string(),
  newExpiryDeadline: z.string(),
  reason: z.string(),
});

export type ExpiryDeadlineAuditDetails = z.infer<typeof ExpiryDeadlineAuditDetailsSchema>;

/**
 * Returns the parsed `{ previousExpiryDeadline, newExpiryDeadline, reason }`
 * for a well-formed `reservation.expiry_deadline_updated` event, or `null`
 * for a different action or malformed/incomplete details (missing keys, or
 * fields present with the wrong type).
 */
export function parseExpiryDeadlineAuditDetails(
  action: string,
  details: Record<string, unknown>,
): ExpiryDeadlineAuditDetails | null {
  if (action !== EXPIRY_DEADLINE_UPDATED_ACTION) return null;
  const result = ExpiryDeadlineAuditDetailsSchema.safeParse(details);
  return result.success ? result.data : null;
}
